import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useId,
  forwardRef,
  type ForwardedRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactElement,
  type FC,
} from 'react'
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form'
import { composeRefs } from '@radix-ui/react-compose-refs'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { useIMask } from 'react-imask'
import type { FactoryOpts, MaskedNumberOptions } from 'imask'
import clsx from 'clsx'
import { isRHFProps } from '@/core/utils/form'
import { useInputGroupPosition } from '@/common/components/input-group'
import * as styles from './Input.css'
import { maskPresets, type MaskPreset } from './maskPresets'

// =============================================================================
// Types
// =============================================================================

type InputBaseProps = {
  /** Label text (required) */
  label: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Show error styling (red border/label) */
  error?: boolean
  /** Whether the field is optional (shows "(Optional)" indicator) */
  optional?: boolean
  /** Whether to show the "(Optional)" indicator when optional is true. Defaults to true. */
  showOptional?: boolean
  /** Icon element to show on the left */
  leftIcon?: ReactNode
  /** Icon element to show on the right */
  rightIcon?: ReactNode
  /** Static prefix text shown before input value (e.g., "$") */
  prefix?: string
  /** Static suffix text shown after input value (e.g., "%") */
  suffix?: string
  /** Additional class name for the wrapper */
  className?: string
  /** Input mask - preset name or custom IMask options */
  mask?: MaskPreset | FactoryOpts
  /**
   * Called when masked value changes (for standalone mode with mask).
   * Receives (unmaskedValue, maskedValue).
   */
  onAccept?: (unmaskedValue: string, maskedValue: string) => void
  /**
   * Hint to disable browser and password manager autofill.
   * Sets autocomplete="off" and vendor-specific attributes (1Password, LastPass, etc.)
   * Note: Browsers may still ignore this in some cases.
   */
  disableAutofill?: boolean
}

// Props for standalone usage (no react-hook-form)
type InputStandaloneProps = InputBaseProps &
  Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'size'>

// Props for react-hook-form usage
type InputRHFProps<T extends FieldValues, N extends FieldPath<T>> = InputBaseProps & {
  /** Field name for react-hook-form */
  name: N
  /** Control object from useForm */
  control: Control<T>
} & Omit<ComponentPropsWithoutRef<'input'>, 'className' | 'size' | 'name' | 'value' | 'onChange' | 'onBlur'>

export type InputProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> =
  | InputStandaloneProps
  | InputRHFProps<T, N>

// =============================================================================
// Component
// =============================================================================

function InputInner<T extends FieldValues, N extends FieldPath<T>>(
  props: InputProps<T, N>,
  forwardedRef: ForwardedRef<HTMLInputElement>
) {
  const {
    label,
    size = 'md',
    error: errorProp,
    optional,
    showOptional = true,
    leftIcon,
    rightIcon,
    prefix,
    suffix,
    className,
    placeholder,
    disabled,
    mask,
    onAccept,
    disableAutofill,
    ...restProps
  } = props

  // Generate unique IDs for accessibility
  const generatedId = useId()
  const inputId = ('id' in restProps && restProps.id) ? restProps.id : generatedId
  const labelId = `${inputId}-label`

  // InputGroup integration
  const groupPosition = useInputGroupPosition()

  // Track if input has value (for label floating)
  // Initialize from props to prevent animation on mount
  const [hasValue, setHasValue] = useState(() => {
    if ('defaultValue' in restProps && restProps.defaultValue) return true
    if ('value' in restProps && restProps.value) return true
    return false
  })
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Track mount state to disable transitions on initial render
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    // Use requestAnimationFrame to ensure styles are applied before enabling transitions
    requestAnimationFrame(() => setMounted(true))
  }, [])

  // Check if this is a number input - we handle number type ourselves to avoid browser quirks
  const isNumberInput = 'type' in restProps && restProps.type === 'number'

  // Check if this is a date/time input - browsers show placeholder text we need to handle
  const inputType = 'type' in restProps ? restProps.type : 'text'
  const isDateTimeInput = inputType === 'date' || inputType === 'time' || inputType === 'datetime-local'

  // Get min/max for number inputs
  const numericMin = isNumberInput && 'min' in restProps ? Number(restProps.min) : undefined
  const numericMax = isNumberInput && 'max' in restProps ? Number(restProps.max) : undefined

  // Get mask options from preset, custom, or auto-generate for number inputs
  const maskOptions = useMemo((): FactoryOpts | null => {
    // For type="number", automatically create a number mask
    if (isNumberInput) {
      const opts: MaskedNumberOptions = {
        mask: Number,
        scale: 0, // No decimals for integer inputs
        // min/max control whether negative numbers are allowed
        // If min is negative or undefined, negatives are allowed
        min: numericMin ?? Number.MIN_SAFE_INTEGER,
        max: numericMax ?? Number.MAX_SAFE_INTEGER,
      }
      return opts
    }

    if (!mask) return null
    if (typeof mask === 'string' && mask in maskPresets) {
      return maskPresets[mask as MaskPreset]
    }
    if (typeof mask === 'object') {
      return mask
    }
    return null
  }, [mask, isNumberInput, numericMin, numericMax])

  // Check if the mask is numeric (returns number instead of string)
  const isNumericMask = useMemo(() => {
    if (isNumberInput) return true
    if (!maskOptions) return false
    // Check if mask option is Number (IMask Number mask)
    return (maskOptions as MaskedNumberOptions).mask === Number
  }, [isNumberInput, maskOptions])

  // RHF integration
  let rhfField: {
    value?: string | number
    onChange?: (value: string | number | null | undefined) => void
    onBlur?: () => void
    ref?: Ref<HTMLInputElement>
    name?: string
  } | null = null
  let fieldError = false

  if (isRHFProps(props)) {
    const { name, control } = props as InputRHFProps<T, N>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field, fieldState } = useController({ name, control })
    rhfField = {
      value: field.value ?? '',
      onChange: field.onChange,
      onBlur: field.onBlur,
      ref: field.ref,
      name: field.name,
    }
    fieldError = !!fieldState.error
  }

  // Default mask that allows any input (used when no mask specified)
  const defaultMaskOpts: FactoryOpts = { mask: /.*/ }

  // useIMask hook for masked inputs
  // Note: We always call useIMask to satisfy Rules of Hooks,
  // but only use its functionality when maskOptions is set
  const {
    ref: imaskRef,
    maskRef,
    setUnmaskedValue,
    unmaskedValue,
  } = useIMask(maskOptions ?? defaultMaskOpts, {
    onAccept: (value, maskRef) => {
      const unmasked = maskRef.unmaskedValue
      setHasValue(!!value)

      // For RHF, update with unmasked value
      // For numeric masks, convert to number to match form schema types
      if (rhfField?.onChange) {
        if (isNumericMask && unmasked !== '') {
          const numericValue = parseFloat(unmasked)
          rhfField.onChange(isNaN(numericValue) ? null : numericValue)
        } else if (isNumericMask && unmasked === '') {
          // Use null instead of undefined - RHF treats undefined as "use defaultValue"
          rhfField.onChange(null)
        } else {
          rhfField.onChange(unmasked)
        }
      }

      // Call onAccept callback for standalone mode
      if (onAccept) {
        onAccept(unmasked, value)
      }

      // Also call standalone onChange if provided (for controlled inputs with masks)
      if ('onChange' in restProps && restProps.onChange && maskOptions) {
        const syntheticEvent = {
          target: { value: unmasked },
          currentTarget: { value: unmasked },
        } as ChangeEvent<HTMLInputElement>
        restProps.onChange(syntheticEvent)
      }
    },
  })

  // Sync RHF value to masked input
  useEffect(() => {
    if (!maskOptions || !rhfField) return

    const mask = maskRef.current

    // Handle null/undefined/empty - clear the masked input
    if (rhfField.value == null || rhfField.value === '') {
      if (mask && mask.unmaskedValue !== '') {
        // Directly set mask value to ensure DOM updates
        mask.unmaskedValue = ''
        mask.updateValue()
      }
      setHasValue(false)
      return
    }

    // Convert to string for imask (handles numeric form values)
    const stringValue = String(rhfField.value)
    // Only update if the unmasked value differs
    if (mask && stringValue !== mask.unmaskedValue) {
      mask.unmaskedValue = stringValue
      mask.updateValue()
    }
    setHasValue(true)
  }, [rhfField?.value, maskOptions, rhfField, maskRef])

  // Sync standalone value prop to masked input
  const standaloneValue = 'value' in restProps ? restProps.value : undefined
  useEffect(() => {
    if (standaloneValue !== undefined && maskOptions && !rhfField) {
      const stringValue = String(standaloneValue ?? '')
      // Only update if the value differs to avoid loops
      if (stringValue !== unmaskedValue) {
        setUnmaskedValue(stringValue)
      }
    }
  }, [standaloneValue, unmaskedValue, setUnmaskedValue, maskOptions, rhfField])

  // Determine if we have an error (prop or RHF)
  const hasError = errorProp ?? fieldError

  // Determine if label should be floated
  // Check both state and direct values to handle initial render correctly
  const hasPlaceholder = !!placeholder
  const hasAnyValue = hasValue || !!rhfField?.value || !!('value' in restProps && restProps.value)
  // For date/time inputs: float label when focused or has value (not when empty + unfocused)
  const shouldFloatLabel = hasPlaceholder || hasAnyValue || (isDateTimeInput && isFocused)

  // For date/time inputs: hide the browser placeholder when empty and unfocused
  const hideDateTimePlaceholder = isDateTimeInput && !hasAnyValue && !isFocused

  // Handle change for non-masked inputs
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasValue(!!e.target.value)

    // For RHF without mask
    if (rhfField?.onChange && !maskOptions) {
      rhfField.onChange(e.target.value)
    }

    // Call standalone onChange if provided
    if ('onChange' in restProps && restProps.onChange) {
      restProps.onChange(e)
    }
  }

  // Handle keydown for number inputs - arrow up/down to increment/decrement
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // For number inputs, handle arrow up/down to increment/decrement
    if (isNumberInput && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault()
      const input = e.currentTarget
      const currentValue = input.value === '' ? 0 : parseFloat(input.value)
      const step = e.key === 'ArrowUp' ? 1 : -1
      let newValue = currentValue + step

      // Clamp to min/max
      if (numericMin !== undefined && newValue < numericMin) newValue = numericMin
      if (numericMax !== undefined && newValue > numericMax) newValue = numericMax

      const newValueStr = String(newValue)

      // Update via IMask
      setUnmaskedValue(newValueStr)

      // Trigger RHF onChange with number value for numeric inputs
      if (rhfField?.onChange) {
        rhfField.onChange(newValue)
      }

      // Trigger standalone onChange
      if ('onChange' in restProps && restProps.onChange) {
        const syntheticEvent = {
          target: { value: newValueStr },
          currentTarget: { value: newValueStr },
        } as ChangeEvent<HTMLInputElement>
        restProps.onChange(syntheticEvent)
      }
    }

    // Call external onKeyDown handler
    if ('onKeyDown' in restProps && restProps.onKeyDown) {
      restProps.onKeyDown(e)
    }
  }

  // Handle focus
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if ('onFocus' in restProps && restProps.onFocus) {
      restProps.onFocus(e)
    }
  }

  // Handle blur
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (rhfField?.onBlur) {
      rhfField.onBlur()
    }
    if ('onBlur' in restProps && restProps.onBlur) {
      restProps.onBlur(e)
    }
  }

  // Check initial value on mount
  useEffect(() => {
    if (inputRef.current) {
      setHasValue(!!inputRef.current.value)
    }
  }, [])

  // Update hasValue when RHF value changes (for non-masked inputs)
  useEffect(() => {
    if (rhfField?.value !== undefined && !maskOptions) {
      setHasValue(!!rhfField.value)
    }
  }, [rhfField?.value, maskOptions])

  // Update hasValue when standalone controlled value changes (for non-masked inputs)
  const standaloneValueProp = 'value' in restProps ? restProps.value : undefined
  useEffect(() => {
    if (!rhfField && !maskOptions && standaloneValueProp !== undefined) {
      setHasValue(!!standaloneValueProp)
    }
  }, [rhfField, maskOptions, standaloneValueProp])

  // Also check for defaultValue
  useEffect(() => {
    if ('defaultValue' in restProps && restProps.defaultValue) {
      setHasValue(true)
    }
  }, [restProps])

  // Merge refs - for masked inputs, include imaskRef
  const mergedRef = maskOptions
    ? composeRefs(forwardedRef, inputRef, imaskRef, rhfField?.ref)
    : composeRefs(forwardedRef, inputRef, rhfField?.ref)

  // Build input props
  const inputProps: ComponentPropsWithoutRef<'input'> & Record<string, unknown> = {
    ...restProps,
    id: inputId,
    placeholder: placeholder,
    disabled,
    className: clsx(
      styles.input,
      shouldFloatLabel && styles.inputWithFloatedLabel
    ),
    // Hide browser placeholder for date/time inputs when empty and unfocused
    style: hideDateTimePlaceholder ? { opacity: 0 } : undefined,
    onChange: maskOptions ? undefined : handleChange, // IMask handles onChange
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    name: rhfField?.name ?? ('name' in restProps ? restProps.name : undefined),
    // Accessibility attributes
    // Only use aria-labelledby if aria-label isn't explicitly provided
    'aria-labelledby': restProps['aria-label'] ? undefined : labelId,
    'aria-invalid': hasError || undefined,
    'aria-required': !optional || undefined,
    // Autofill disable attributes
    ...(disableAutofill && {
      autoComplete: 'off',
      'data-1p-ignore': true,        // 1Password
      'data-lpignore': 'true',       // LastPass
      'data-form-type': 'other',     // Generic hint
    }),
    // For number inputs, use text type with numeric inputMode to avoid browser quirks
    ...(isNumberInput && {
      type: 'text',
      inputMode: 'decimal' as const,
      pattern: '-?[0-9]*',
    }),
  }

  // For non-masked inputs, set value explicitly
  // For masked inputs, remove value from props (IMask controls value via ref)
  if (!maskOptions) {
    inputProps.value = rhfField?.value ?? ('value' in restProps ? restProps.value : undefined)
  } else {
    // IMask handles value internally - don't pass value prop to avoid
    // "controlled without onChange" warning
    delete inputProps.value
  }

  return (
    <div className={clsx(
      styles.wrapper({ size, error: hasError, disabled }),
      groupPosition === 'start' && styles.groupWrapperStart,
      groupPosition === 'middle' && styles.groupWrapperMiddle,
      !mounted && styles.noTransition,
      className
    )}>
      <div
        className={clsx(
          styles.inputContainer,
          groupPosition === 'start' && styles.groupPositionStart,
          groupPosition === 'middle' && styles.groupPositionMiddle,
          groupPosition === 'end' && styles.groupPositionEnd,
          hasError && styles.inputContainerError,
          disabled && styles.inputContainerDisabled
        )}
      >
        {/* Left icon */}
        {leftIcon && (
          <div className={clsx(styles.leftIcon, disabled && styles.leftIconDisabled)}>
            {leftIcon}
          </div>
        )}

        {/* Content area with input and floating label */}
        <div className={styles.content}>
          {/* Prefix (shown when there's a value) */}
          {prefix && hasAnyValue && (
            <span className={styles.prefix}>{prefix}</span>
          )}

          {/* Input */}
          <input ref={mergedRef} {...inputProps} />

          {/* Suffix (shown at end of input area when there's a value) */}
          {suffix && hasAnyValue && (
            <span className={styles.suffix}>{suffix}</span>
          )}

          {/* Floating label */}
          <label
            id={labelId}
            htmlFor={inputId}
            className={clsx(
              styles.label,
              shouldFloatLabel && styles.labelFloated,
              hasError && styles.labelError,
              disabled && styles.labelDisabled
            )}
          >
            {label}
            {/* Screen reader only: announce if optional */}
            {optional && <VisuallyHidden.Root> (optional)</VisuallyHidden.Root>}
          </label>

          {/* Optional indicator (visual only, sr-only text is in label) */}
          {optional && showOptional && (
            <span
              aria-hidden="true"
              className={clsx(
                styles.optional,
                shouldFloatLabel && styles.optionalFloated
              )}
            >
              (Optional)
            </span>
          )}
        </div>

        {/* Right icon */}
        {rightIcon && (
          <div className={clsx(styles.rightIcon, disabled && styles.rightIconDisabled)}>
            {rightIcon}
          </div>
        )}
      </div>
    </div>
  )
}

// ForwardRef wrapper with generic support
export const Input = forwardRef(InputInner) as <
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>
>(
  props: InputProps<T, N> & { ref?: ForwardedRef<HTMLInputElement> }
) => ReactElement

// Display name for debugging
(Input as FC).displayName = 'Input'

